import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import i18n from '../i18n/config'
import { getCountryConfig } from '../lib/country-config'
import { PaktLockup } from '../components/pakt'
import type { AuthResponse } from '../types'

export default function Login() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const googleError = searchParams.get('error')

  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const res = await api.post<AuthResponse>('/api/auth/login', { email, senha })
      localStorage.setItem('pakt_token', res.token)
      if (res.user) {
        localStorage.setItem('pakt_user', JSON.stringify({ nome: res.user.name, email: res.user.email, cpf: res.user.cpf || null, telefone: res.user.telefone || null, countryCode: res.user.countryCode || 'BR' }))
      }
      try {
        const uStr = localStorage.getItem('pakt_user')
        if (uStr) {
          const u = JSON.parse(uStr)
          const config = getCountryConfig(u.countryCode || 'BR')
          if (config?.locale) {
            i18n.changeLanguage(config.locale)
            localStorage.setItem('pakt_language', config.locale)
          }
        }
      } catch {}
      navigate('/dashboard')
    } catch (err: unknown) {
      let message = t('auth.loginError', 'Incorrect email or password')
      if (err && typeof err === 'object' && 'body' in err) {
        const body = (err as { body: Record<string, unknown> }).body
        if (body?.message) {
          message = String(body.message)
        } else if (body?.error) {
          message = String(body.error)
        }
      }
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/"><PaktLockup size={22} /></Link>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            {t('login.title', 'Sign in to your account')}
          </h1>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                {t('login.email', 'Email')}
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                placeholder="maria@example.com"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="senha" className="block text-sm font-medium text-gray-700">
                  {t('login.password', 'Password')}
                </label>
                <Link to="/forgot-password" className="text-xs text-indigo-600 hover:text-indigo-800">
                  {t('login.forgotPassword', 'Forgot password?')}
                </Link>
              </div>
              <input
                id="senha"
                type="password"
                required
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {loading ? t('login.loading', 'Signing in...') : t('login.submit', 'Sign In')}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-6 relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs text-gray-400">
              <span className="px-2 bg-white">{t('login.or', 'ou continue com')}</span>
            </div>
          </div>

          {googleError && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-lg text-sm text-center">
              {t('login.googleError', 'Não foi possível entrar com o Google. Tente novamente.')}
            </div>
          )}

          {/* Google Sign-In */}
          <a
            href="/api/auth/google"
            className="mt-4 w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition text-sm font-medium text-gray-700"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {t('login.googleSignIn', 'Entrar com Google')}
          </a>

          <p className="mt-4 text-center text-sm text-gray-600">
            {t('login.noAccount', "Don't have an account?")}{' '}
            <Link to="/register" className="text-indigo-600 hover:text-indigo-800 font-medium">
              {t('login.registerLink', 'Create one')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
